import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Star, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import DisclosureBar from "../components/site/DisclosureBar";
import EmailOptin from "../components/site/EmailOptin";
import { FadeUp, MaskedLines, Stagger, staggerItem } from "../components/site/Reveal";
import { getHub } from "../data/hubs";
import { getPlatform } from "../data/platforms";
import { motion } from "framer-motion";

const HubPage = () => {
  const { slug } = useParams();
  const hub = getHub(slug);

  useEffect(() => {
    if (hub) document.title = `${hub.title} (2026) | Top Email Marketing Tools`;
    window.scrollTo(0, 0);
  }, [slug, hub]);

  if (!hub) {
    return (
      <main className="mx-auto max-w-3xl px-6 pt-48 pb-32 text-center" data-testid="hub-not-found">
        <h1 className="font-display text-4xl font-black">Page not found</h1>
        <Link to="/" className="mt-6 inline-block text-volt underline">Back home</Link>
      </main>
    );
  }

  return (
    <main>
      <div className="pt-[76px]"><DisclosureBar /></div>

      <header className="relative overflow-hidden pt-20 pb-16">
        {hub.image && (
          <div className="absolute inset-0">
            <img src={hub.image} alt="" className="h-full w-full object-cover opacity-[0.1]" />
            <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/85 to-ink" />
          </div>
        )}
        <div className="relative mx-auto max-w-6xl px-6">
          <p className="overline-label mb-5" data-testid="hub-overline">{hub.overline} · Updated July 2026</p>
          <h1 className="max-w-3xl font-display text-5xl font-black leading-[1.05] tracking-tighter md:text-6xl" data-testid="hub-title">
            <MaskedLines lines={[hub.title]} />
          </h1>
          <FadeUp delay={0.3}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg">{hub.lede}</p>
          </FadeUp>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-10" data-testid="hub-criteria">
        <FadeUp><p className="overline-label mb-8">How We Judged</p></FadeUp>
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {hub.criteria.map((c, i) => (
            <motion.div key={i} variants={staggerItem} className="rounded-2xl border border-white/[0.07] bg-cardink p-6" data-testid={`criterion-${i}`}>
              <span className="font-serif-accent text-3xl italic text-volt/50">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 font-display font-bold">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/55">{c.desc}</p>
            </motion.div>
          ))}
        </Stagger>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16" data-testid="hub-picks">
        <FadeUp><h2 className="mb-12 font-display text-4xl font-black tracking-tighter">Our ranked picks</h2></FadeUp>
        <div className="space-y-10">
          {hub.picks.map((pick, i) => {
            const p = getPlatform(pick.slug);
            if (!p) return null;
            return (
              <FadeUp key={pick.slug} delay={i * 0.06}>
                <div className="grid gap-8 rounded-2xl border border-white/[0.07] bg-cardink p-8 transition-transform duration-300 hover:-translate-y-1 md:grid-cols-[auto_1fr_auto] md:p-10" data-testid={`hub-pick-${pick.slug}`}>
                  <div className="flex items-start gap-5">
                    <span className="font-serif-accent text-5xl italic text-volt/40">#{i + 1}</span>
                    <span className="flex h-14 w-14 items-center justify-center rounded-xl font-display text-base font-extrabold" style={{ backgroundColor: `${p.hue}1f`, color: p.hue }}>{p.monogram}</span>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-2xl font-bold">{p.name}</h3>
                      <span className="flex items-center gap-1 text-sm text-white/60"><Star className="h-3.5 w-3.5 fill-volt text-volt" />{p.rating}</span>
                    </div>
                    <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-volt"><CheckCircle2 className="h-4 w-4" />{pick.verdict}</p>
                    <p className="mt-4 max-w-2xl text-sm leading-[1.8] text-white/60">{pick.why}</p>
                    <p className="mt-3 text-xs text-white/40">From {p.startingPrice}/mo · {p.freePlan}</p>
                  </div>
                  <div className="flex flex-row gap-3 md:flex-col md:items-end md:justify-center">
                    <a href={p.affiliateUrl} target="_blank" rel="noopener noreferrer sponsored" data-testid={`hub-cta-${pick.slug}`} className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-volt px-6 py-3 text-xs font-bold text-ink transition-colors hover:bg-volt-hover">
                      Start Free Trial <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                    <Link to={`/reviews/${pick.slug}`} data-testid={`hub-review-${pick.slug}`} className="self-center text-xs text-white/50 underline-offset-2 hover:text-volt hover:underline">
                      Read full review
                    </Link>
                  </div>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-16" data-testid="hub-faq">
        <FadeUp><h2 className="mb-10 font-display text-3xl font-black tracking-tighter">Common questions</h2></FadeUp>
        <Accordion type="single" collapsible>
          {hub.faq.map((f, i) => (
            <AccordionItem key={i} value={`hub-faq-${i}`} className="border-white/[0.07]">
              <AccordionTrigger data-testid={`hub-faq-trigger-${i}`} className="text-left font-display text-base font-bold hover:text-volt hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent data-testid={`hub-faq-content-${i}`} className="text-sm leading-relaxed text-white/60">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="border-t border-white/5 bg-surface py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <FadeUp>
            <h2 className="font-display text-3xl font-black tracking-tighter">Get the free checklist before you choose</h2>
            <p className="mt-3 text-sm text-white/55">Ten steps to a healthy email program — whichever platform you pick.</p>
            <div className="mx-auto mt-8 max-w-xl text-left"><EmailOptin source={`hub-${slug}`} /></div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
};

export default HubPage;
